import {
  automaticRiskReason,
  CLIENT_TYPE_LABELS,
  type Client,
  effectiveRiskLevel,
  missingFields,
  RISK_LEVEL_LABELS,
  RISK_LEVELS,
  RISK_REASON_LABELS,
  type RiskLevel,
} from "@pcj/shared";
import { FolderOpen, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Banner, Button, EmptyState, Pill, SectionTitle, Table, type Tone, td, th } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { plural } from "../../lib/format";
import { ClientDialog } from "./ClientDialog";
import { useCompany } from "./CompanyLayout";

const RISK_TONES: Record<RiskLevel, Tone> = { low: "green", medium: "amber", high: "red" };

export function ClientsTab() {
  const { clients, operations, thresholds, can, readOnly, actions, documentsByClient } = useCompany();
  const [dialog, setDialog] = useState<{ open: boolean; client: Client | null }>({ open: false, client: null });

  const operationCount = new Map<string, number>();
  const identificationRequired = new Set<string>();
  for (const op of operations) {
    operationCount.set(op.clientId, (operationCount.get(op.clientId) ?? 0) + 1);
    if (op.amount >= thresholds.identification) identificationRequired.add(op.clientId);
  }

  function remove(client: Client) {
    if (window.confirm(`¿Eliminar a ${client.name}? Quedará registrado en la bitácora.`)) {
      actions.deleteClient.mutate(client.id);
    }
  }

  return (
    <div className="space-y-4">
      <SectionTitle
        title="Expedientes de clientes"
        subtitle="Identificación de clientes · función 1 del art. 41 RCG"
        actions={
          can("capture") &&
          !readOnly && (
            <Button icon={Plus} onClick={() => setDialog({ open: true, client: null })}>
              Nuevo cliente
            </Button>
          )
        }
      />
      {actions.deleteClient.error && <Banner tone="error">{errorMessage(actions.deleteClient.error)}</Banner>}
      {actions.updateClient.error && !dialog.open && (
        <Banner tone="error">{errorMessage(actions.updateClient.error)}</Banner>
      )}

      {clients.length === 0 ? (
        <EmptyState icon={Users} title="Todavía no hay clientes registrados">
          Registra a cada cliente antes de capturar sus operaciones.
        </EmptyState>
      ) : (
        <Table>
          <thead className="bg-gray-50">
            <tr>
              <th className={th}>Cliente</th>
              <th className={th}>Tipo</th>
              <th className={th}>Operaciones</th>
              <th className={th}>Expediente</th>
              <th className={th}>Documentos</th>
              <th className={th}>Grado de Riesgo</th>
              <th className={th} aria-label="Acciones" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {clients.map((client) => {
              const missing = missingFields(client);
              const automatic = automaticRiskReason(client);
              const level = effectiveRiskLevel(client);
              const required = identificationRequired.has(client.id);
              return (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className={td}>
                    <button
                      type="button"
                      className="text-left font-medium text-gray-900 hover:underline"
                      onClick={() => setDialog({ open: true, client })}
                    >
                      {client.name}
                    </button>
                    {client.pep && (
                      <span className="ml-2">
                        <Pill tone="red">PPE</Pill>
                      </span>
                    )}
                  </td>
                  <td className={`${td} text-gray-600`}>{CLIENT_TYPE_LABELS[client.type]}</td>
                  <td className={`${td} text-gray-600`}>{operationCount.get(client.id) ?? 0}</td>
                  <td className={td}>
                    {missing.length === 0 ? (
                      <Pill tone="green">Completo</Pill>
                    ) : (
                      <span
                        title={
                          required
                            ? "Obligatorio: ya alcanzó el umbral de identificación"
                            : "Se exige al alcanzar el umbral de identificación"
                        }
                      >
                        <Pill tone={required ? "red" : "neutral"}>
                          Falta{missing.length === 1 ? "" : "n"} {plural(missing.length, "dato", "datos")}
                        </Pill>
                      </span>
                    )}
                  </td>
                  <td className={td}>
                    <Link
                      to={`${client.id}/documents`}
                      className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-gray-700 hover:underline"
                      aria-label={`Documentos de ${client.name}`}
                    >
                      <FolderOpen className="h-4 w-4 text-gold-500" aria-hidden />
                      {documentsByClient.get(client.id)?.length ?? 0}
                    </Link>
                  </td>
                  <td className={td}>
                    {automatic ? (
                      <span title={RISK_REASON_LABELS[automatic]}>
                        <Pill tone="red">Alto · automático</Pill>
                      </span>
                    ) : can("manageCompliance") ? (
                      <select
                        aria-label={`Grado de Riesgo de ${client.name}`}
                        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium"
                        value={client.riskLevel}
                        disabled={actions.updateClient.isPending}
                        onChange={(e) =>
                          actions.updateClient.mutate({ id: client.id, riskLevel: e.target.value as RiskLevel })
                        }
                      >
                        {RISK_LEVELS.map((r) => (
                          <option key={r} value={r}>
                            {RISK_LEVEL_LABELS[r]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Pill tone={RISK_TONES[level]}>{RISK_LEVEL_LABELS[level]}</Pill>
                    )}
                  </td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Pencil}
                      onClick={() => setDialog({ open: true, client })}
                      aria-label={`Ver expediente de ${client.name}`}
                    >
                      {can("capture") && !readOnly ? "Editar" : "Ver"}
                    </Button>
                    {can("delete") && (
                      <Button
                        variant="danger"
                        size="sm"
                        icon={Trash2}
                        onClick={() => remove(client)}
                        aria-label={`Eliminar a ${client.name}`}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      <ClientDialog
        open={dialog.open}
        client={dialog.client}
        onClose={() => setDialog({ open: false, client: null })}
      />
    </div>
  );
}
